import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = ["Manufacturing", "Healthcare", "Hospitality", "Construction", "Logistics", "Field Services", "Automotive", "Commerce"];
const DEPTS = ["Operations", "Quality", "Maintenance", "Safety", "HR", "Finance"];

export default function SOPBuilder() {
  const nav = useNavigate();
  const [topic, setTopic] = useState("");
  const [industry, setIndustry] = useState("Manufacturing");
  const [department, setDepartment] = useState("Operations");
  const [details, setDetails] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Enter a topic");
    setBusy(true);
    setContent("");
    try {
      const token = localStorage.getItem("ruleai_token");
      const res = await fetch(`${API}/sops/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topic, industry, department, details }),
      });
      if (!res.ok || !res.body) {
        toast.error("Generation failed");
        setBusy(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setContent(acc);
      }
    } catch (e) {
      toast.error("Generation error");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!content.trim() || !topic.trim()) return toast.error("Nothing to save");
    setSaving(true);
    try {
      const { data: saved } = await api.post("/sops", {
        title: topic,
        department,
        content,
        tags: [industry.toLowerCase()],
      });
      toast.success("SOP saved to library");
      nav(`/app/sops/${saved.id}`);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="sop-builder-page">
      <div>
        <div className="overline text-accent">/ AI SOP BUILDER</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Draft an SOP.</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Powered by Claude Sonnet 4.6. Streaming, audit-ready.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Config panel */}
        <div className="lg:col-span-2 border border-border bg-card p-6 space-y-4 h-fit">
          <div>
            <Label>Topic</Label>
            <Input
              className="rounded-sm mt-1.5"
              placeholder="e.g., Preventive Maintenance for Line 3"
              data-testid="sop-topic"
              value={topic} onChange={(e) => setTopic(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="sop-industry"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="sop-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPTS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Extra context (optional)</Label>
            <Textarea
              rows={4}
              className="rounded-sm mt-1.5"
              placeholder="Frequency, equipment IDs, safety notes, references…"
              data-testid="sop-details"
              value={details} onChange={(e) => setDetails(e.target.value)}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={generate} disabled={busy}
              data-testid="sop-generate-btn"
              className="rounded-sm bg-accent hover:bg-accent/90 text-white flex-1"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {busy ? "Generating…" : "Generate with AI"}
            </Button>
            <Button
              onClick={save} disabled={saving || !content}
              data-testid="sop-save-btn"
              variant="outline"
              className="rounded-sm"
            >
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-3">
          <div className="terminal border border-zinc-800 rounded-sm p-6 min-h-[520px] max-h-[70vh] overflow-auto" data-testid="sop-output">
            <div className="overline text-accent mb-4">◉ SOP OUTPUT</div>
            <pre className="text-xs whitespace-pre-wrap leading-relaxed text-orange-200">
{content || "// Awaiting generation. Configure topic on the left and click 'Generate with AI'."}
{busy && <span className="kinetic" />}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
